import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
	Shipment as mobizShipment,
	ShipmentStatus,
	SummarizedShipmentStatus,
} from '../../../../@posturinn/client-react';
import { fetchShipment } from '../../../../actions/shipmentsActions';
import { PAYMENT_ERROR } from '../../../../constants/actions/paymentActions';
import { getQueryParam } from '../../../../helpers/url/urlHelpers';
import myPostWrapper from '../../../../hoc/myPostWrapper';
import useLocations from '../../../../hooks/useLocations';
import usePaymentErrorFromQuery from '../../../../hooks/usePaymentErrorFromQuery';
import { ReactComponent as IconNoShipment } from '../../../../icons/new-product.svg';
import IMyShipmentsViewModel from '../../../../interfaces/myShipments/myShipments';
import IPaymentReducer from '../../../../interfaces/reducers/paymentReducer';
import IShipmentsReducer from '../../../../interfaces/reducers/shipmentsReducer';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import ISettingsViewModel from '../../../../interfaces/umbracoNodes/settingsViewModel';
import { IStore } from '../../../../reducers';
import MyShipmentsTileLoader from '../../../base/misc/myShipmentsTileLoader';
import { H1 } from '../../../base/typography';
import StepFeedback, {
	StepFeedbackInfo,
} from '../../../registerShipment/registerShipmentForm/stepFeedback/stepFeedback';
import * as StyledModals from '../../modals/myShipmentsModals.styles';
import * as StyledMyShipments from '../myShipments.styles';
import ShipmentCard from '../shipmentCard';
import * as StyledShipmentList from '../shipmentList/shipmentList.styles';

interface Props {
	page: IMyShipmentsViewModel;
}

const Shipment: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const query = getQueryParam('id');
	const open = getQueryParam('open');
	const { settings }: { settings: ISettingsViewModel } = useSelector(({ pageReducer }) => pageReducer);
	usePaymentErrorFromQuery(error => ({ type: PAYMENT_ERROR, payload: error }));
	const { locations, locationsLoading } = useLocations();
	const paymentReducer: IPaymentReducer = useSelector<IStore, IPaymentReducer>(store => store.paymentReducer);
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { isLoggedIn } = userProfileReducer;
	const { payError } = paymentReducer;
	const [info, setInfo] = useState<StepFeedbackInfo>(null);
	
	const {
		shipment,
		shipmentError,
		shipmentLoading,
	}: { shipment: mobizShipment; shipmentError: boolean; shipmentLoading: boolean } = useSelector<
		IStore,
		IShipmentsReducer
	>(({ shipmentsReducer }) => shipmentsReducer);

	useEffect(() => {
		if (query && !shipment) {
			dispatch(fetchShipment(query));
		}
	}, [query, dispatch, shipment]);
	
	useEffect(() => {
		setInfo({
			shipmentId: shipment?.id,
			clientId: 'posturinn-web',
			domestic: shipment?.recipient.address.country === 'IS',
			loggedIn: isLoggedIn,
		});
		// eslint-disable-next-line
	}, []);
	
	
	return (
		<>
			<StyledMyShipments.HeaderMain>
				<H1>{t('navigation.shipment')}</H1>
			</StyledMyShipments.HeaderMain>

			{payError && (
				<StyledModals.ErrorMessage>
					<FaExclamationCircle />
					{payError}
				</StyledModals.ErrorMessage>
			)}
			{shipmentLoading && (
				<div>
					<MyShipmentsTileLoader />
				</div>
			)}
			{!shipmentLoading && !shipment && (
				<StyledShipmentList.ShipmentCard simpleIcon blue>
					<IconNoShipment />
					{t('page-overview.noShipmentFound')}
				</StyledShipmentList.ShipmentCard>
			)}
			{!shipmentLoading && !shipmentError && shipment && !locationsLoading && locations && (
				<>
					{shipment.status === ShipmentStatus.PreTransit && settings.Values.RegisterShipmentFeedback && (
						<StepFeedback info={info} />
					)}
					<StyledShipmentList.Container>
						<ShipmentCard
							key={shipment.id}
							shipment={shipment}
							locations={locations}
							delivered={shipment.summarizedStatus === SummarizedShipmentStatus.Completed}
							open={open !== null}
							includeHidden={false}
							showActions={false}
						/>
					</StyledShipmentList.Container>
				</>
			)}
		</>
	);
};

export default myPostWrapper(Shipment);
